
import React, { useState, useRef, useEffect } from 'react';
import type { FC, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import ReactDOM from 'react-dom';

import type { User } from '@/api/types/user';
import { UserType } from '@/api/types/user';

import { AvatarIcon } from './avatar-icon';
import { LogoutIcon } from './logout-icon';
import { OrganizationIcon } from './organization-icon';
import { SettingsIcon } from './settings-icon';
import { MENU_Z_INDEX } from './z-index';

interface UserMenuProps {
    user: User | null;
    onLogout: () => void;
    onAccountSettings: () => void;
    onOrganizationSettings: () => void;
}

export const UserMenu: FC<UserMenuProps> = ({ user, onLogout, onAccountSettings, onOrganizationSettings }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const toggleMenu = (e: ReactMouseEvent) => {
        e.stopPropagation();
        setIsOpen((prev) => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getPosition = () => {
        const menuRect = ref.current?.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const width = 256;
        const iconWidth = 40;

        let left = menuRect?.left ?? 0;
        const top = menuRect?.bottom ?? 0;

        if (left + width > windowWidth) {
            left = left - width;
        }
        return { top: top + 10, left: left + iconWidth };
    };

    return (
        <div className="relative inline-block" ref={ref}>
            <button type="button" onClick={toggleMenu}>
                <div className="rounded-full bg-blue-60 hover:bg-opacity-75 h-10 w-10 content-center">
                    <p className="text-sm text-white text-center">
                        {user?.firstName?.charAt(0).toUpperCase()}
                        {user?.lastName?.charAt(0).toUpperCase()}
                    </p>
                </div>
            </button>
            {isOpen &&
                ReactDOM.createPortal(
                    <div
                        className="absolute w-64 bg-gray-100 shadow-xl rounded-md transition-all duration-100 ease-in-out overflow-y-scroll opacity-100 visible"
                        style={{ zIndex: MENU_Z_INDEX, ...getPosition() }}
                        ref={menuRef}
                    >
                        <div className="p-3 flex flex-col gap-2">
                            <p className="text-xxs text-gray-60">PROFILE</p>
                            <div className="flex flex-row">
                                <div className="rounded-full bg-blue-60 h-10 w-10 content-center">
                                    <p className="text-sm text-white text-center">
                                        {user?.firstName?.charAt(0).toUpperCase()}
                                        {user?.lastName?.charAt(0).toUpperCase()}
                                    </p>
                                </div>
                                <div className="flex flex-col pl-2">
                                    <p className="text-sm text-white">
                                        {user?.firstName} {user?.lastName}
                                    </p>
                                    <p className="text-xs text-gray-50">{user?.email}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="flex flex-row items-center justify-center gap-1 w-full py-2 text-white border-[1px] border-gray-80 hover:bg-gray-90 text-xs rounded-md"
                                onClick={() => {
                                    setIsOpen(false);
                                    onAccountSettings?.();
                                }}
                            >
                                <AvatarIcon />
                                Account Settings
                            </button>
                        </div>
                        <hr className="bg-gray-80 border-gray-80 opacity-20" />
                        <div className="p-3 flex flex-col gap-2">
                            <p className="text-xxs text-gray-60">COMPANY</p>
                            <div className="flex flex-row">
                                {user?.organization?.logo ? (
                                    <img
                                        src={user?.organization.logo}
                                        alt="Organization Logo"
                                        className="h-10 w-10 rounded-full"
                                    />
                                ) : (
                                    <OrganizationIcon />
                                )}
                                <div className="flex flex-col pl-2">
                                    <p className="text-sm text-white">{user?.organization?.name ?? ''}</p>
                                    <p className="text-xs text-gray-50">Role: {user?.type}</p>
                                </div>
                            </div>
                            {user?.type === UserType.ADMIN && (
                                <button
                                    type="button"
                                    className="flex flex-row items-center justify-center gap-1 w-full py-2 text-white border-[1px] border-gray-80 hover:bg-gray-90 text-xs rounded-md"
                                    onClick={() => {
                                        setIsOpen(false);
                                        onOrganizationSettings?.();
                                    }}
                                >
                                    <SettingsIcon />
                                    Admin Controls
                                </button>
                            )}
                        </div>
                        <hr className="bg-gray-80 border-gray-80 opacity-20" />
                        <div className="p-3 hover:bg-gray-90">
                            <button
                                type="button"
                                className="flex flex-row items-center gap-1.5 w-full py-2 text-red-50 text-xs"
                                onClick={onLogout}
                            >
                                <LogoutIcon />
                                Logout
                            </button>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};
